import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Button, Skeleton } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
    Eye, MousePointerClick, Trash2, ArrowLeft, ArrowRight, BarChart3, RefreshCw,
    CalendarDays, CalendarRange, ChevronDown, Check, Inbox, AlertTriangle,
} from 'lucide-react';
import Calling from '../api/Calling';
import { formatView } from '../components/formatView';
import { ClickChart, ViewChart } from '../components/charts/ViewClickChart';
import getISTDate from '../components/getISTDate';

// Brand palette
const BRAND = '#34C759';
const BRAND_HOVER = '#2db84d';
const BRAND_SOFT = 'rgba(52,199,89,0.10)';
const BRAND_RING = 'rgba(52,199,89,0.45)';

// Premium green-accent outlined field styling for the date pickers
const dateFieldSx = {
    minWidth: 168,
    '& .MuiInputLabel-root': { fontSize: '0.8rem', fontWeight: 500, color: '#6b7280' },
    '& .MuiInputLabel-root.Mui-focused': { color: BRAND },
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: '#fff',
        fontSize: '0.875rem',
        fontWeight: 600,
        color: '#111827',
        transition: 'border-color .15s ease, box-shadow .15s ease',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_RING },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND, borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 4px ${BRAND_SOFT}` },
    },
    '& .MuiInputAdornment-root .MuiIconButton-root': {
        color: '#9ca3af',
        borderRadius: '8px',
        transition: 'color .15s ease, background-color .15s ease',
        '&:hover': { color: BRAND, backgroundColor: BRAND_SOFT },
    },
};
const applyBtnSx = {
    backgroundColor: BRAND,
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: '0 6px 16px -6px rgba(52,199,89,0.6)',
    whiteSpace: 'nowrap',
    px: 2.75,
    height: 40,
    '&:hover': { backgroundColor: BRAND_HOVER, boxShadow: '0 8px 20px -6px rgba(52,199,89,0.7)' },
};

// Premium, fully-branded theme for the calendar popup (keys verified for @mui/x-date-pickers@9.6).
const pickerTheme = createTheme({
    palette: { primary: { main: BRAND } },
    shape: { borderRadius: 12 },
    typography: { fontWeightMedium: 600 },
    components: {
        MuiPickerPopper: {
            styleOverrides: {
                paper: {
                    borderRadius: 16,
                    marginTop: 6,
                    border: '1px solid #eef0f2',
                    boxShadow: '0 18px 50px -12px rgba(16,24,40,0.22), 0 8px 16px -8px rgba(16,24,40,0.10)',
                    overflow: 'hidden',
                },
            },
        },
        MuiPickersCalendarHeader: {
            styleOverrides: {
                root: { paddingLeft: 18, paddingRight: 14, marginTop: 12, marginBottom: 4 },
                label: { fontWeight: 700, fontSize: '0.95rem', color: '#111827' },
                switchViewButton: { color: '#374151', '&:hover': { backgroundColor: BRAND_SOFT, color: BRAND } },
            },
        },
        MuiPickersArrowSwitcher: {
            styleOverrides: {
                button: { color: '#6b7280', borderRadius: 10, '&:hover': { backgroundColor: BRAND_SOFT, color: BRAND } },
            },
        },
        MuiDayCalendar: {
            styleOverrides: {
                weekDayLabel: { fontWeight: 700, fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase' },
            },
        },
        MuiPickerDay: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    borderRadius: 10,
                    transition: 'background-color .12s ease, color .12s ease',
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&:focus': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': {
                        backgroundColor: BRAND,
                        color: '#fff',
                        boxShadow: '0 4px 10px -3px rgba(52,199,89,0.6)',
                        '&:hover': { backgroundColor: BRAND_HOVER },
                        '&:focus': { backgroundColor: BRAND_HOVER },
                    },
                },
                today: {
                    border: `1.5px solid ${BRAND}`,
                    color: BRAND,
                    fontWeight: 700,
                    '&:not(.Mui-selected)': { backgroundColor: BRAND_SOFT },
                },
            },
        },
        MuiYearCalendar: {
            styleOverrides: {
                button: {
                    borderRadius: 10,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': { backgroundColor: BRAND, color: '#fff', '&:hover': { backgroundColor: BRAND_HOVER }, '&:focus': { backgroundColor: BRAND_HOVER } },
                },
            },
        },
        MuiMonthCalendar: {
            styleOverrides: {
                button: {
                    borderRadius: 10,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': { backgroundColor: BRAND, color: '#fff', '&:hover': { backgroundColor: BRAND_HOVER }, '&:focus': { backgroundColor: BRAND_HOVER } },
                },
            },
        },
    },
});

const pickerSlots = {
    openPickerIcon: () => <CalendarDays size={18} />,
    leftArrowIcon: () => <ArrowLeft size={18} />,
    rightArrowIcon: () => <ArrowRight size={18} />,
    switchViewIcon: () => <ChevronDown size={18} />,
};

const QUICK_RANGES = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'thisWeek', label: 'This Week' },
    { key: 'thisMonth', label: 'This Month' },
    { key: 'last6Months', label: 'Last 6 Months' },
    { key: 'thisYear', label: 'This Year' },
];

const ViewsDashbord = () => {
    const [loader, setLoader] = useState(false);
    const [startDate, setStartDate] = useState(getISTDate());
    const [endDate, setEndDate] = useState(getISTDate());
    const [viewsData, setViewsData] = useState([]);
    const [clicksData, setClicksData] = useState([]);
    const [totalViews, setTotalViews] = useState(0);
    const [totalClicks, setTotalClicks] = useState(0);
    const [selectState, setSelectState] = useState('today');
    const [showConfirm, setShowConfirm] = useState(false);
    // Draft (uncommitted) date-range values for the pickers — applied only on "Apply" click
    const [draftStart, setDraftStart] = useState(dayjs(getISTDate()));
    const [draftEnd, setDraftEnd] = useState(dayjs(getISTDate()));
    const { id } = useParams();
    const navigate = useNavigate();

    const fetchData = async () => {
        setLoader(true);
        try {
            const response = await Calling.adminViewsDashbord(id, startDate, endDate);
            if (response?.status) {
                setViewsData(response?.views_data);
                setClicksData(response?.clicks_data);
                setTotalClicks(response?.totalClicks || 0);
                setTotalViews(response?.totalViews || 0);
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    // Keep the picker drafts in sync whenever the committed range changes
    useEffect(() => {
        setDraftStart(startDate ? dayjs(startDate) : null);
        setDraftEnd(endDate ? dayjs(endDate) : null);
    }, [startDate, endDate]);

    const handleDateRangeFilter = (range) => {
        const todayUTC = new Date();

        // Convert any date to IST (UTC + 5:30) and format YYYY-MM-DD
        const toIST = (date) => {
            const istOffset = 5.5 * 60 * 60 * 1000; // 5 hours 30 mins in milliseconds
            const istDate = new Date(date.getTime() + istOffset);
            return istDate.toISOString().split('T')[0];
        };

        let start, end;

        switch (range) {
            case 'today':
                start = toIST(todayUTC);
                end = start;
                setSelectState('today');
                break;

            case 'yesterday':
                const yesterdayUTC = new Date(todayUTC);
                yesterdayUTC.setDate(todayUTC.getDate() - 1);
                start = toIST(yesterdayUTC);
                end = start;
                setSelectState('yesterday');
                break;

            case 'thisWeek':
                const firstDayOfWeekUTC = new Date(todayUTC);
                firstDayOfWeekUTC.setDate(todayUTC.getDate() - todayUTC.getDay());
                const lastDayOfWeekUTC = new Date(firstDayOfWeekUTC);
                lastDayOfWeekUTC.setDate(firstDayOfWeekUTC.getDate() + 6);

                start = toIST(firstDayOfWeekUTC);
                end = toIST(lastDayOfWeekUTC);
                setSelectState('thisWeek');
                break;

            case 'thisMonth':
                const firstDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);
                const lastDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);

                start = toIST(firstDayOfMonthUTC);
                end = toIST(lastDayOfMonthUTC);
                setSelectState('thisMonth');
                break;

            case 'last6Months':
                const firstDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() - 5, 1);
                const lastDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);

                start = toIST(firstDayLast6UTC);
                end = toIST(lastDayLast6UTC);
                setSelectState('last6Months');
                break;

            case 'thisYear':
                const firstDayOfYearUTC = new Date(todayUTC.getFullYear(), 0, 1);
                const lastDayOfYearUTC = new Date(todayUTC.getFullYear(), 11, 31);

                start = toIST(firstDayOfYearUTC);
                end = toIST(lastDayOfYearUTC);
                setSelectState('thisYear');
                break;

            default:
                start = '';
                end = '';
                setSelectState('range');
        }

        setStartDate(start);
        setEndDate(end);
        // keep picker drafts aligned with the quick-range commit so the Apply button doesn't flash
        setDraftStart(start ? dayjs(start) : null);
        setDraftEnd(end ? dayjs(end) : null);
    };

    // Commit the picked draft range — applies only on "Apply" click
    const handleApplyDateRange = () => {
        if (!draftStart || !draftStart.isValid() || !draftEnd || !draftEnd.isValid()) {
            toast.error('Please select both start and end dates', { autoClose: 2000 });
            return;
        }
        const today = dayjs().endOf('day');
        if (draftStart.isAfter(today) || draftEnd.isAfter(today)) {
            toast.error('Future dates are not allowed', { autoClose: 2000 });
            return;
        }
        if (draftStart.isAfter(draftEnd, 'day')) {
            toast.error('Start date cannot be after end date', { autoClose: 2000 });
            return;
        }
        setStartDate(draftStart.format('YYYY-MM-DD'));
        setEndDate(draftEnd.format('YYYY-MM-DD'));
        setSelectState('range');
    };

    const clearData = async () => {
        setShowConfirm(false);
        setLoader(true);
        try {
            const response = await Calling.deleteViewsAnalytic(id, startDate, endDate);

            if (response?.status) {
                toast.success(response?.message || 'Analytics data deleted successfully');
            } else {
                toast.error(response?.message || 'Failed to delete analytics data');
            }
        } catch (error) {
            console.error('Error deleting data:', error);
            toast.error('Failed to delete analytics data');
        } finally {
            setLoader(false);
            fetchData();
        }
    };

    // Full chart skeleton only on the very first load (before any data is present)
    const initialLoading = loader && totalViews === 0 && totalClicks === 0 && viewsData.length === 0 && clicksData.length === 0;
    // Apply button appears only when the picker draft differs from the applied range
    const isRangeDirty =
        (draftStart && draftStart.isValid() ? draftStart.format('YYYY-MM-DD') : '') !== (startDate || '') ||
        (draftEnd && draftEnd.isValid() ? draftEnd.format('YYYY-MM-DD') : '') !== (endDate || '');

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-full overflow-x-hidden text-main-font"
        >
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-4 gap-3">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 rounded-xl border border-main-border bg-main-white text-main-subfont hover:text-main-font hover:bg-main-bg transition-colors"
                        title="Go back"
                    >
                        <ArrowLeft size={18} />
                    </button>
                    <div className="p-2.5 bg-main-blue rounded-xl shadow-lg shadow-main-blue/30 text-main-white">
                        <BarChart3 size={22} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-main-font flex items-center gap-2">
                            View &amp; Click Analytics
                            {loader && !initialLoading && (
                                <RefreshCw size={16} className="animate-spin text-main-subfont" />
                            )}
                        </h1>
                        <p className="text-sm text-main-subfont">Per-ad views & clicks over the selected range</p>
                    </div>
                </div>

                <button
                    onClick={() => setShowConfirm(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-main-white bg-main-delete hover:bg-main-delete/90 shadow-sm transition-colors"
                    title="Delete analytics for the selected range"
                >
                    <Trash2 size={16} /> Clear Data
                </button>
            </div>

            <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                {/* Date Range Filters */}
                <div className="p-4 md:p-6">
                    <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-5">
                        {/* Quick Range — modern segmented control */}
                        <div>
                            <p className="text-[11px] font-semibold text-main-subfont uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <CalendarDays size={13} /> Quick Range
                            </p>
                            <div className="inline-flex flex-wrap items-center gap-1 bg-main-bg border border-main-border rounded-xl p-1">
                                {QUICK_RANGES.map((r) => {
                                    const active = selectState === r.key;
                                    return (
                                        <button
                                            key={r.key}
                                            onClick={() => handleDateRangeFilter(r.key)}
                                            className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${active
                                                ? 'bg-main-blue text-main-white shadow-sm shadow-main-blue/30'
                                                : 'text-main-subfont hover:text-main-font hover:bg-main-white'
                                                }`}
                                        >
                                            {r.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Custom Range — themed MUI date pickers + Apply (applies only on click) */}
                        <div>
                            <p className="text-[11px] font-semibold text-main-subfont uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <CalendarRange size={13} /> Custom Range
                            </p>
                            <ThemeProvider theme={pickerTheme}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 bg-main-bg border border-main-border rounded-2xl p-2.5 shadow-sm">
                                        <DatePicker
                                            label="From"
                                            value={draftStart}
                                            onChange={(v) => setDraftStart(v)}
                                            format="DD MMM YYYY"
                                            disableFuture
                                            maxDate={draftEnd || undefined}
                                            slots={pickerSlots}
                                            slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
                                        />
                                        <span className="hidden sm:flex items-center justify-center text-main-subfont">
                                            <ArrowRight size={16} />
                                        </span>
                                        <DatePicker
                                            label="To"
                                            value={draftEnd}
                                            onChange={(v) => setDraftEnd(v)}
                                            format="DD MMM YYYY"
                                            disableFuture
                                            minDate={draftStart || undefined}
                                            slots={pickerSlots}
                                            slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleApplyDateRange}
                                            sx={{ ...applyBtnSx, display: isRangeDirty ? 'inline-flex' : 'none' }}
                                            startIcon={<Check size={16} />}
                                        >
                                            Apply
                                        </Button>
                                    </div>
                                </LocalizationProvider>
                            </ThemeProvider>
                        </div>
                    </div>

                    {/* Applied-range indicator */}
                    {selectState === 'range' && startDate && endDate && (
                        <div className="mt-3 inline-flex items-center gap-2 text-xs text-main-subfont bg-main-bg border border-main-border rounded-lg px-3 py-1.5">
                            <CalendarRange size={13} />
                            Showing <span className="font-semibold text-main-font">{dayjs(startDate).format('DD MMM YYYY')}</span>
                            <ArrowRight size={12} />
                            <span className="font-semibold text-main-font">{dayjs(endDate).format('DD MMM YYYY')}</span>
                        </div>
                    )}
                </div>

                <hr className="border-main-border" />

                {/* Card Section */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 sm:p-6">
                    {/* Total Views */}
                    <div className="rounded-2xl border border-main-border bg-main-white p-5 shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wider text-main-subfont">Views</p>
                                <h4 className="text-3xl font-bold tracking-tight text-main-font mt-1.5">
                                    {totalViews < 10 ? '0' + totalViews : formatView(totalViews)}
                                </h4>
                            </div>
                            <div className="shrink-0 rounded-xl p-3 bg-blue-50 text-blue-600">
                                <Eye size={24} />
                            </div>
                        </div>
                    </div>

                    {/* Total Clicks */}
                    <div className="rounded-2xl border border-main-border bg-main-white p-5 shadow-sm hover:shadow-md transition-all duration-300">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="text-[11px] font-semibold uppercase tracking-wider text-main-subfont">Clicks</p>
                                <h4 className="text-3xl font-bold tracking-tight text-main-font mt-1.5">
                                    {totalClicks < 10 ? '0' + totalClicks : formatView(totalClicks)}
                                </h4>
                            </div>
                            <div className="shrink-0 rounded-xl p-3 bg-purple-50 text-purple-600">
                                <MousePointerClick size={24} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Charts */}
                <div className="p-4 sm:p-6 pt-0 space-y-6">
                    {initialLoading ? (
                        <>
                            <Skeleton variant="rounded" height={320} sx={{ borderRadius: '12px' }} />
                            <Skeleton variant="rounded" height={320} sx={{ borderRadius: '12px' }} />
                        </>
                    ) : (totalViews > 0 || totalClicks > 0) ? (
                        <>
                            {totalViews > 0 && (
                                <div className="h-64 sm:h-72 md:h-80 lg:h-96">
                                    <ViewChart ViewData={viewsData} />
                                </div>
                            )}
                            {totalClicks > 0 && (
                                <div className="h-64 sm:h-72 md:h-80 lg:h-96">
                                    <ClickChart ClickData={clicksData} />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="w-full flex flex-col items-center justify-center py-12 px-4">
                            <div className="bg-main-bg rounded-full p-5 mb-4 text-main-subfont">
                                <Inbox size={36} />
                            </div>
                            <h3 className="text-lg font-semibold text-main-font mb-1 text-center">No Analytics Found</h3>
                            <p className="text-sm text-main-subfont text-center max-w-md">
                                No views or clicks recorded for the selected date range. Try a different period.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <AnimatePresence>
                {showConfirm && (
                    <motion.div
                        key="views-delete-modal"
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="bg-main-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
                            initial={{ opacity: 0, scale: 0.95, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 10 }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className="bg-main-delete text-white p-5 flex items-center gap-3">
                                <AlertTriangle size={24} />
                                <h2 className="text-xl font-bold">Delete Analytics Data</h2>
                            </div>
                            <div className="p-6 space-y-4">
                                <p className="text-main-subfont text-sm">
                                    You are about to permanently delete the analytics (views &amp; clicks) for the
                                    selected range. The ad's total counters will also be adjusted accordingly.
                                    This action cannot be undone.
                                </p>

                                <div className="bg-main-bg border border-main-border rounded-xl p-4 space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs font-semibold uppercase text-main-subfont">Selected Range</span>
                                        <span className="text-sm font-medium text-main-font">
                                            {startDate === endDate ? startDate : `${startDate}  →  ${endDate}`}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                                            <p className="text-xs text-main-subfont">Views</p>
                                            <p className="text-lg font-bold text-blue-600">{formatView(totalViews)}</p>
                                        </div>
                                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                                            <p className="text-xs text-main-subfont">Clicks</p>
                                            <p className="text-lg font-bold text-purple-600">{formatView(totalClicks)}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex gap-3 justify-end pt-2">
                                    <button
                                        onClick={() => setShowConfirm(false)}
                                        className="px-5 py-2.5 rounded-xl font-medium border border-main-border text-main-subfont hover:bg-main-bg transition"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={clearData}
                                        className="px-5 py-2.5 rounded-xl font-medium bg-main-delete text-white hover:bg-main-delete/90 transition flex items-center gap-2"
                                    >
                                        <Trash2 size={16} /> Delete
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default ViewsDashbord;
